import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useProperty } from '@eti/providers';
import OrianaChat from './oriana/OrianaChat';
import GenesysChat from './genesys/GenesysChat';
import { closeGenesysChat, openGenesysChat } from './genesys/utils';
import { setOrianaChatClosed } from './oriana/utils';
import type { ChatType } from './models';
import { openWebMessagesChat } from './webMessages/utils';
import { useWebMessagesChat } from './webMessages/useWebMessagesChat';

type ChatContextType = {
  openChat: (input: ChatType | null) => void;
  setGenesysExtraInfo: Dispatch<SetStateAction<Record<string, string> | null>>;
};

const ChatContext = createContext<ChatContextType | null>(null);

interface ChatProviderProps {
  children?: ReactNode;
  supportedChatTypes: ChatType[];
  userName?: string;
}

const ChatProvider = ({ children, supportedChatTypes, userName }: ChatProviderProps) => {
  const { p } = useProperty();
  const [openChatType, setOpenChatType] = useState<ChatType | null>(null);
  const [genesysExtraInfo, setGenesysExtraInfo] = useState<Record<string, string> | null>(null);
  const isOrianaChatEnabled = p('Chatbot.Oriana.Enabled') && supportedChatTypes.includes('oriana');
  const isGenesysChatEnabled = p('Chat.Widget.Enabled') && supportedChatTypes.includes('genesys');
  const isWebMessagesEnabled = p('Chat.WebMessages.Enabled');

  useWebMessagesChat(isWebMessagesEnabled, userName);

  const handleOpenOriana = useCallback(() => {
    if (isOrianaChatEnabled && openChatType === 'genesys') {
      closeGenesysChat();
    }
    if (isOrianaChatEnabled && openChatType !== 'oriana') {
      setOpenChatType('oriana');
    }
    if (!isOrianaChatEnabled) {
      setOpenChatType('genesys');
      openGenesysChat();
    }
  }, [isOrianaChatEnabled, openChatType]);

  const handleOpenGenesys = () => {
    if (isOrianaChatEnabled && openChatType === 'oriana') {
      setOrianaChatClosed();
    }
    setOpenChatType('genesys');
    openGenesysChat();
  };

  const handleOpenWebMessages = () => {
    openWebMessagesChat(genesysExtraInfo);
  };

  const openChat = (chatType: ChatType | null) => {
    if (isWebMessagesEnabled) {
      handleOpenWebMessages();
    } else {
      switch (chatType) {
        case 'oriana':
          handleOpenOriana();
          break;
        case 'genesys':
          handleOpenGenesys();
          break;
        default:
          setOpenChatType(chatType);
      }
    }
  };

  return (
    <ChatContext.Provider value={{ openChat, setGenesysExtraInfo }}>
      {children}
      {!isWebMessagesEnabled && (
        <>
          {isOrianaChatEnabled && (
            <OrianaChat isOpen={openChatType === 'oriana'} setOpenChatType={setOpenChatType} />
          )}
          {isGenesysChatEnabled && <GenesysChat extraInfo={genesysExtraInfo} />}
        </>
      )}
    </ChatContext.Provider>
  );
};

const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);

  if (isEmpty(context)) {
    throw new Error('useChat must be used within an ChatProvider');
  }

  return { ...context };
};

export { ChatProvider, useChat };
